// ═══════════════════════════════════════════════════════════════════════════
// Firebase client init — milbrands-cowork
// ═══════════════════════════════════════════════════════════════════════════
// Protocolo Bombai Fase 2a: se agregó proyecto staging.
// El cliente selecciona la config correcta según el hostname donde corre:
//   - *-staging.web.app / *-staging.firebaseapp.com → proyecto staging
//   - cualquier otro (milbrands-cowork.web.app, dominio custom, localhost
//     sin override) → proyecto prod
//
// Las API keys de Firebase web NO son secretas por diseño — son identifica-
// dores del proyecto. La seguridad real vive en:
//   1. Firestore Security Rules
//   2. Authorized domains en Firebase Auth (Authentication → Settings →
//      Authorized domains)
//   3. Cloud Functions que validan auth.uid + requireEmpresaAccess()
//
// Para debug local apuntando a un proyecto específico, setear en la URL:
//    ?env=staging  o  ?env=prod
// (queda en localStorage hasta que se cambie explícitamente)
// ═══════════════════════════════════════════════════════════════════════════

import { initializeApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import { getAuth, GoogleAuthProvider } from 'firebase/auth'
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getFunctions } from 'firebase/functions'
import { diagnosticarAppCheck } from './appCheckDiagnostico'
// Las configs por proyecto viven en una HOJA (T10b tajada 2): la página pública
// del evento las necesita para App Check por REST y no carga este módulo.
import { PROD_CONFIG, STAGING_CONFIG } from './firebaseConfig'

// ─── Selección de proyecto ────────────────────────────────────────────────
// NAVEGADOR de verdad = window CON location. No alcanza chequear solo window:
// el shim del CI de deploy (gaxios-undici-shim.cjs) crea un `window` global
// pelado (solo fetch) para firebase-tools, y el smoke test del bundle del
// runner corre bajo ese mismo proceso → `window.location.hostname` explotaba
// (deploy 30ea029, 15-jul). Cloud Functions no tiene window; el shim tiene
// window sin location; ambos deben caer al camino HEADLESS.
const esNavegador = typeof window !== 'undefined' && typeof window.location !== 'undefined'

/**
 * PURA. El proyecto de un cargador HEADLESS (16-jul-2026 — runner backend del
 * modo automático: el bundle del pipeline corre en Cloud Functions con el MISMO
 * código). Lo elige quien carga el bundle, por `BOMBAI_FIREBASE_ENV`, ANTES de
 * requerirlo. Fuera de 'staging' | 'prod' LANZA (report M8GJ36QHlGiL8T9bDgYH):
 * hasta el 20-oct elegía 'prod', así que una carga nueva que se olvidara de la
 * var escribía contra producción sin error. Es la otra punta de
 * tNAEYzCIVPfQtml5PfQ6, que cerró lo mismo en el runner (`entornoDelBundle`).
 */
export function entornoHeadless(valor) {
  if (valor === 'staging' || valor === 'prod') return valor
  throw new Error(`firebase.js headless: BOMBAI_FIREBASE_ENV es ${JSON.stringify(valor)}, y tiene que ser 'staging' o 'prod' — sin eso no se elige proyecto`)
}

function pickEnv() {
  if (!esNavegador) {
    // El try envuelve SÓLO la lectura: si envolviera también la decisión, el
    // catch se tragaría el throw de `entornoHeadless`.
    let nodeEnv
    try {
      // eslint-disable-next-line no-undef -- `process` en código de browser: existe bajo Vite (lo reemplaza el build) y por eso la guarda `typeof`
      nodeEnv = typeof process !== 'undefined' ? process.env?.BOMBAI_FIREBASE_ENV : undefined
    } catch (_) { /* sin process: queda undefined y lanza abajo */ }
    return entornoHeadless(nodeEnv)
  }

  // 1) Override explícito via query param ?env=staging o ?env=prod
  try {
    const params = new URLSearchParams(window.location.search)
    const qp = params.get('env')
    if (qp === 'staging' || qp === 'prod') {
      try { localStorage.setItem('bombai.firebaseEnv', qp) } catch (_) {}
      return qp
    }
    const stored = localStorage.getItem('bombai.firebaseEnv')
    if (stored === 'staging' || stored === 'prod') return stored
  } catch (_) { /* SSR o storage deshabilitado */ }

  // 1.5) Default de build (solo dev local): VITE_FIREBASE_ENV en .env.local.
  // En prod/CI la var no existe → no afecta. El query param y localStorage
  // siguen teniendo prioridad para overridear puntualmente.
  const buildEnv = import.meta.env.VITE_FIREBASE_ENV
  if (buildEnv === 'staging' || buildEnv === 'prod') return buildEnv

  // 2) Auto-detect por hostname
  const h = window.location.hostname || ''
  if (h.includes('staging')) return 'staging'
  return 'prod'
}

const env = pickEnv()
const firebaseConfig = env === 'staging' ? STAGING_CONFIG : PROD_CONFIG

// Log visible en consola para saber contra qué proyecto está autenticando
// (útil cuando algo no anda y uno no sabe si está mirando prod o staging).
if (esNavegador) {
  // eslint-disable-next-line no-console
  console.info(
    `%c[Firebase] env=${env} · project=${firebaseConfig.projectId}`,
    env === 'staging'
      ? 'background:#f59e0b;color:#000;padding:2px 6px;border-radius:3px;font-weight:bold'
      : 'background:#10b981;color:#000;padding:2px 6px;border-radius:3px;font-weight:bold'
  )
}

const app = initializeApp(firebaseConfig)

// ─── SEC-B2: Firebase App Check con reCAPTCHA Enterprise ────────────────────
// Cada request a callables (`httpsCallable`) y a Firestore se acompaña
// automáticamente de un token de App Check que valida que viene de NUESTRA
// app oficial (no de cURL, scripts, Postman ni otras webs). Si el callable
// tiene `enforceAppCheck: true` en backend, requests sin token válido son
// rechazadas con 401 antes de llegar al handler.
//
// VITE_RECAPTCHA_SITE_KEY es la "site key" de reCAPTCHA Enterprise (es
// pública, ok hardcodearla en el bundle). La key se crea en GCP Console →
// Security → reCAPTCHA Enterprise.
//
// En dev sin la key, App Check no se inicializa (pass-through).
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY
if (esNavegador && RECAPTCHA_SITE_KEY) {
  try {
    // Para debug local sin reCAPTCHA real, podés setear este flag y App Check
    // genera tokens debug que tenés que registrar en Firebase Console.
    // (Solo para desarrollo, NO usar en prod/staging.)
    if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG === 'true') {
      // Si pasás un UUID fijo en VITE_APPCHECK_DEBUG_TOKEN, App Check usa SIEMPRE
      // ese token (estable entre reloads/browsers) — lo registrás una sola vez en
      // Firebase Console. Si no, `true` genera uno nuevo y lo imprime en consola.
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true
    }
    // STAGING (deployado): debug token manual vía localStorage. Permite testear
    // callables con `enforceAppCheck:true` cuando reCAPTCHA Enterprise rechaza
    // el canje (`exchangeRecaptchaEnterpriseToken` → 403) por trust score del
    // proyecto degradado — ej. post-pentest, ver ESTADO_ACTUAL.md "AppCheck
    // throttle 24h tras pentest".
    //
    // Uso: en la consola del browser, sobre la app de staging, correr:
    //   localStorage.setItem('FIREBASE_APPCHECK_DEBUG_TOKEN', '<UUID>')
    // donde <UUID> es un token registrado en Firebase Console → App Check →
    // Apps → Milbrands CoWork → ⋮ → Administrar tokens de depuración.
    // Para desactivarlo: localStorage.removeItem('FIREBASE_APPCHECK_DEBUG_TOKEN')
    //
    // Gateado a staging: el bundle de PROD nunca incluye este bypass.
    else if (firebaseConfig.projectId === 'milbrands-cowork-staging') {
      let debugToken = null
      try {
        debugToken = window.localStorage.getItem('FIREBASE_APPCHECK_DEBUG_TOKEN')
      } catch (_) { /* storage deshabilitado */ }
      if (debugToken) {
        self.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken
        console.warn(
          '%c[App Check] DEBUG TOKEN activo (localStorage · solo staging) — App Check bypasseado para este browser',
          'background:#dc2626;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold'
        )
      }
    }
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    })
    console.info('[App Check] inicializado con reCAPTCHA Enterprise')
  } catch (err) {
    // No-fatal acá, pero FATAL para la app: desde el ADR 2026-09-05 (T2c)
    // TODO onCall exige App Check por default, así que sin init casi ningún
    // callable contesta. El diagnóstico de abajo y el grito por sesión (#1386)
    // son la única señal; el runbook es runbooks/appcheck-caido.md.
    console.warn('[App Check] no se pudo inicializar:', err?.message)
  }
}

// ── Por qué App Check quedó (o no) inicializado — report oz7FcUdw ───────────
// El `if` de arriba se saltea EN SILENCIO cuando no hay site key: el
// `console.warn` del catch sólo dispara si `initializeAppCheck` tira, y en ese
// camino ni se lo llama. Un bundle deployado sin la key hace que los callables
// con `enforceAppCheck` contesten 401 sin que nada diga por qué.
//
// Acá sólo se CALCULA el diagnóstico y se expone como valor. No se emite desde
// este módulo, y es a propósito, por dos razones:
//   1. `logger.js` importa `db`/`auth` de este mismo archivo — emitir desde
//      acá arma un ciclo de imports y deja `auth` en TDZ.
//   2. `console.*` está bloqueado por `scripts/check-no-console-log.sh` sobre
//      líneas agregadas; el canal del repo es `@core/lib/logger`, que además
//      es el único que persiste a `clientLogs` (o sea, el único que alguien lee).
// La emisión vive en `AuthContext.jsx`, que ya importa las dos cosas.
//
// Cero cambio de comportamiento: la condición del `if` de App Check es la misma.
export const diagnosticoAppCheck = diagnosticarAppCheck({
  esNavegador,
  siteKey: RECAPTCHA_SITE_KEY,
  esDev: import.meta.env.DEV,
})

export const auth = getAuth(app)
// Firestore con cache persistente en IndexedDB → datos instantáneos en cargas
// siguientes. HEADLESS (runner backend): en Node no hay IndexedDB — cache en
// memoria (default), mismo cliente, mismas rules.
export const db = !esNavegador
  ? initializeFirestore(app, {})
  : initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    })
export const storage = getStorage(app)

// Cloud Functions client — siempre usa `us-central1` (dominio default
// `cloudfunctions.net`). Los callables (`httpsCallable`) NO pasan por
// Cloud Armor / Load Balancer porque:
//   1. El LB solo tiene path matchers para los 4 endpoints públicos de B1
//      (mlNotifications, webhookRouter, googleOAuthCallback,
//      conexionOAuthCallback). Cualquier callable que no esté ahí caería
//      al backend default y rompería el wire protocol del SDK Callable.
//   2. Los callables ya tienen defensa app-level robusta: validación de
//      auth (Firebase ID token), CORS whitelist (SEC-F0.2), rate-limit
//      per-user (SEC-F0.6 — 4 tiers), audit log (SEC-F0.4) y MFA opcional
//      (SEC-F0.3 / forzado en B3).
//   3. Los webhooks/OAuth callbacks SÍ pasan por LB (vía `fetch()` directo
//      con `FUNCTIONS_BASE_URL`), porque son los únicos endpoints públicos
//      que reciben tráfico server-to-server.
export const functions = getFunctions(app, 'us-central1')

// Helper para builds de URL de fetch directo a Cloud Functions (onRequest).
// SEC-B1 (mayo 2026): si VITE_FUNCTIONS_BASE_URL está seteado, los fetch
// directos a los 4 endpoints PÚBLICOS pasan por LB + Cloud Armor (WAF +
// geofence + rate-limit + capa C con X-LB-Auth). Si no, fallback al dominio
// default (ej. dev local).
//
// IMPORTANTE: usar FUNCTIONS_BASE_URL SOLO para los 4 endpoints públicos
// rute­ados por el LB (mlNotifications, webhookRouter, googleOAuthCallback,
// conexionOAuthCallback). Para endpoints internos (googleOAuthStart,
// diagnosticarTranscripciones, etc.) que el consultor llama autenticado,
// usar FUNCTIONS_BASE_URL_INTERNAL — porque el LB no tiene URL map para
// esos paths y devuelve "ok" sin headers CORS, rompiendo el preflight.
// SEC-B5 fix #299 (11 mayo 2026).
// Nota: declaramos firebaseProjectId ACÁ (no más abajo) para evitar TDZ
// — FUNCTIONS_BASE_URL_INTERNAL lo usa siempre (sin short-circuit como
// FUNCTIONS_BASE_URL que sí lo tiene gracias al `||`).
export const firebaseProjectId = firebaseConfig.projectId
export const FUNCTIONS_BASE_URL = import.meta.env.VITE_FUNCTIONS_BASE_URL
  || `https://us-central1-${firebaseProjectId}.cloudfunctions.net`
export const FUNCTIONS_BASE_URL_INTERNAL =
  `https://us-central1-${firebaseProjectId}.cloudfunctions.net`
export const googleProvider = new GoogleAuthProvider()

// El dominio `hd` de Google SE SETEA AL LOGIN desde la config (configuracion/auth),
// NO hardcodeado acá (C.1 / login multi-método). Ver AuthContext.login() +
// useAuthConfig.hdParaGoogle. 'solo-milbrands' → hd=dominio Workspace; 'todos' → sin hd.

// Helpers de debug exportables (útil en componentes que quieren saber el env)
// Nota: firebaseProjectId se exporta más arriba (antes de FUNCTIONS_BASE_URL).
export const firebaseEnv = env

export default app
